import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';
import { CommunityNewsOption, AnnouncementsOption } from '../../../components/components/CustomSidebar/NeighborsOptions';
import UserContext from '../../../contexts/UserContext';
import { fetchAllCommunityNews } from '../../services/CommunityNews';
import { fetchAllCommunityAnnouncements } from '../../services/Announcements';
import { CommunityNewsType } from '../../types/CommunityNews';
import { Announcement } from '../../types/Announcement';
import sendToast from '../../../utils/Toast';
import moment from 'moment-timezone';
import './News.less';
import { userFacingDateTimeFormat } from '../../../utils/timezone';
import { NewspaperIcon } from '../icons';

interface NewsItem {
    id: string;
    type: 'news' | 'announcement';
    title: string;
    timestamp: string;
    date: Date;
    data: CommunityNewsType | Announcement;
}

interface NewsProps {
    facilityTimezone: string;
    parentLoading: boolean;
}

const News = ({ facilityTimezone, parentLoading }: NewsProps) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [displayedItems, setDisplayedItems] = useState<NewsItem[]>([]);

    const { userObject } = useContext(UserContext);
    const {
        registrants: { list: listOfResidents, activeRegistrantsIndexes },
    } = userObject;
    const correspondingSpeak2Resident = listOfResidents[activeRegistrantsIndexes[0]];
    const activeResidentId = correspondingSpeak2Resident?._id;
    const isGuestUser = userObject?.userType === 'guest';

    const fetchTodayNews = useCallback(async () => {
        try {
            if (!correspondingSpeak2Resident || !facilityTimezone) return;

            setLoading(true);
            const nowInFacility = moment().tz(facilityTimezone);
            const todayStart = nowInFacility.clone().startOf('day');
            const todayEnd = nowInFacility.clone().endOf('day');

            const newsStartDate = todayStart.clone().format('YYYY-MM-DD');
            // add a day to the end date
            const newsEndDate = todayEnd.clone().add(1, 'day').format('YYYY-MM-DD');

            // Fetch announcements for today
            const announcementsStartISO = todayStart.clone().format('YYYY-MM-DDTHH:mm:ss');
            const announcementsEndISO = todayEnd.clone().format('YYYY-MM-DDTHH:mm:ss');

            // Fetch both news and announcements in parallel
            const [newsResponse, announcementsResponse] = await Promise.all([
                fetchAllCommunityNews(
                    correspondingSpeak2Resident.Facility as string,
                    {
                        startDate: { $lt: newsEndDate },
                        endDate: { $gte: newsStartDate },
                    }
                ).catch((error) => {
                    console.error('Error fetching community news:', error);
                    sendToast('error', error instanceof Error ? error.message : 'Failed to fetch Community News');
                    return [] as CommunityNewsType[];
                }),
                isGuestUser
                    ? Promise.resolve([] as Announcement[])
                    : fetchAllCommunityAnnouncements(
                        correspondingSpeak2Resident.Facility as string,
                        {
                            startTimestampISO: announcementsStartISO,
                            endTimestampISO: announcementsEndISO,
                            residentId: correspondingSpeak2Resident._id as string,
                        }
                    ).catch((error) => {
                        console.error('Error fetching announcements:', error);
                        sendToast('error', error instanceof Error ? error.message : 'Failed to fetch Announcements');
                        return [] as Announcement[];
                    }),
            ]);

            const communityNews: CommunityNewsType[] = newsResponse;
            const announcements: Announcement[] = announcementsResponse;

            // Combine and convert to NewsItem format
            const combinedItems: NewsItem[] = [];

            // Normalize community news (API already returns today's items)
            communityNews.forEach((news, index) => {
                const date = news.startDate ? moment.tz(news.startDate, facilityTimezone).toDate() : new Date();
                const timestamp = news.startDate ? moment.tz(news.startDate, facilityTimezone).format(userFacingDateTimeFormat) : '';
                const fallbackId = `news-${news.srcId || news.signageImageId || news.startDate || news.name || 'item'}-${index}`;
                combinedItems.push({
                    id: news._id || fallbackId,
                    type: 'news',
                    title: news.name || 'Community News',
                    timestamp,
                    date,
                    data: news,
                });
            });

            // Normalize announcements (API already returns today's items)
            announcements.forEach((announcement) => {
                let date: Date;
                let timestamp: string;

                if (announcement.startDate) {
                    date = moment.tz(announcement.startDate, facilityTimezone).toDate();
                    timestamp = moment.tz(announcement.startDate, facilityTimezone).format(userFacingDateTimeFormat);
                } else {
                    date = moment.tz(announcement.DateAdded, facilityTimezone).toDate();
                    timestamp = moment.tz(announcement.DateAdded, facilityTimezone).format(userFacingDateTimeFormat);
                }

                combinedItems.push({
                    id: announcement._id,
                    type: 'announcement',
                    title: announcement.Message,
                    timestamp,
                    date,
                    data: announcement,
                });
            });

            // Sort by date (newest first)
            combinedItems.sort((a, b) => b.date.getTime() - a.date.getTime());
            setDisplayedItems(combinedItems);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to fetch news');
        } finally {
            setLoading(false);
        }
    }, [correspondingSpeak2Resident, facilityTimezone, isGuestUser]);

    useEffect(() => {
        if (facilityTimezone && activeResidentId) {
            fetchTodayNews();
        }
    }, [facilityTimezone, activeResidentId, fetchTodayNews]);

    if (!correspondingSpeak2Resident) {
        return null;
    }

    // The fetch never runs without a timezone, so a stuck initial `loading`
    // flag must not keep the spinner up. That case shows the empty state.
    const canFetch = !!facilityTimezone && !!activeResidentId;
    const isLoading = parentLoading || (canFetch && loading);

    return (
        <div className="news-component home-card-wrap home-card">
            <div className="home-card-header gray">
                <span aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <NewspaperIcon size={18} />
                </span>
                Community News
            </div>
            <div className={`home-card-body home-card-body-scroll${isLoading ? ' is-loading' : ''}`}>
                {isLoading ? (
                    <Loader active inline="centered" />
                ) : displayedItems.length === 0 ? (
                    <div className="home-card-empty">{isGuestUser ? 'No news for today' : 'No news or announcements for today'}</div>
                ) : (
                    displayedItems.map((item) => {
                        const linkTo = item.type === 'news' ? CommunityNewsOption.path : AnnouncementsOption.path;
                        const typeLabel = item.type === 'news' ? 'Community News' : 'Announcements';
                        return (
                            <Link key={item.id} to={linkTo} className="home-news-item">
                                <div className="home-news-meta">
                                    <span>{typeLabel}</span>
                                    <span>{item.timestamp || 'Today'}</span>
                                </div>
                                <div className="home-news-text">{item.title}</div>
                            </Link>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default News;
